import { Injectable, computed, signal } from '@angular/core';
import {
  HdChartTabSnapshotV1,
  createDefaultHdChartTabSnapshot,
  parseHdChartTabsState,
} from './hd-chart-tab-snapshot.models';
import {
  notifyUserDataSavedLocally,
  USER_DATA_APP_STATE_STORAGE_KEY,
} from '@common/user-data-sync/user-data-local-sync';

export const HD_CHART_TABS_STORAGE_KEY = USER_DATA_APP_STATE_STORAGE_KEY;
const PERSIST_DEBOUNCE_MS = 500;

/**
 * Beheert de chart-tabs van de chart environment full page: tablijst, actieve
 * tab en localStorage-persistentie (key `hdChartTabs`, spec §6–§7).
 *
 * Page-provided (géén providedIn root): de pagina levert bij elke operatie het
 * actuele snapshot aan (extract uit de pagina-signals) en past het
 * teruggegeven doel-snapshot zelf toe. De service kent de pagina dus niet.
 *
 * Persistentie: mutaties die de structuur wijzigen (switch/add/close)
 * schrijven direct; `updateActiveSnapshot` (autosave bij edits) debounced.
 */
@Injectable()
export class HdChartTabsService {
  private readonly _tabs = signal<readonly HdChartTabSnapshotV1[]>([]);
  private readonly _activeTabId = signal('');
  private persistTimerId: ReturnType<typeof setTimeout> | null = null;

  readonly tabs = this._tabs.asReadonly();
  readonly activeTabId = this._activeTabId.asReadonly();
  readonly activeTab = computed(
    () => this._tabs().find(tab => tab.id === this._activeTabId()) ?? this._tabs()[0],
  );

  /** Laadt de opslag of maakt één verse tab. Geeft het toe te passen snapshot terug. */
  initialize(): HdChartTabSnapshotV1 {
    const stored = parseHdChartTabsState(localStorage.getItem(HD_CHART_TABS_STORAGE_KEY));
    if (stored) {
      this._tabs.set(stored.tabs);
      this._activeTabId.set(stored.activeTabId);
    } else {
      const tab = createDefaultHdChartTabSnapshot();
      this._tabs.set([tab]);
      this._activeTabId.set(tab.id);
      this.persistNow();
    }
    return this.activeTab();
  }

  /** Autosave van de actieve tab (id blijft die van de tab zelf). */
  updateActiveSnapshot(snapshot: HdChartTabSnapshotV1): void {
    this._tabs.update(tabs =>
      tabs.map(tab => (tab.id === this._activeTabId() ? { ...snapshot, id: tab.id } : tab)),
    );
    this.schedulePersist();
  }

  /**
   * Gericht bijwerken van één tab-snapshot (bv. een achtergrond-tab verversen
   * met het verse library-document na een edit in de chart manager). De id is
   * onaantastbaar; onbekende tabs zijn een no-op. Persisteert direct — de
   * verversing moet ook een page-reload (localStorage-herstel) overleven.
   */
  patchTab(tabId: string, patch: Partial<Omit<HdChartTabSnapshotV1, 'id'>>): void {
    if (!this._tabs().some(tab => tab.id === tabId)) {
      return;
    }
    this._tabs.update(tabs => tabs.map(tab => (tab.id === tabId ? { ...tab, ...patch, id: tab.id } : tab)));
    this.persistNow();
  }

  /**
   * Wisselt naar `targetId`. Bewaart eerst `currentSnapshot` op de huidige
   * actieve tab. Geeft het doel-snapshot terug (toe te passen door de pagina),
   * of `null` wanneer er niets te wisselen valt.
   */
  switchTo(targetId: string, currentSnapshot: HdChartTabSnapshotV1): HdChartTabSnapshotV1 | null {
    if (targetId === this._activeTabId()) {
      return null;
    }
    const target = this._tabs().find(tab => tab.id === targetId);
    if (!target) {
      return null;
    }
    this.updateActiveSnapshot(currentSnapshot);
    this._activeTabId.set(targetId);
    this.persistNow();
    return target;
  }

  /** Bewaart het huidige snapshot, maakt een verse tab en activeert die. */
  addTab(currentSnapshot: HdChartTabSnapshotV1): HdChartTabSnapshotV1 {
    this.updateActiveSnapshot(currentSnapshot);
    const tab = createDefaultHdChartTabSnapshot();
    this._tabs.update(tabs => [...tabs, tab]);
    this._activeTabId.set(tab.id);
    this.persistNow();
    return tab;
  }

  /**
   * Sluit een tab. Geeft het snapshot terug dat de pagina moet toepassen
   * wanneer de actieve tab veranderde (rechterbuur, anders linkerbuur; bij de
   * laatste tab een verse default-tab), of `null` wanneer de actieve tab
   * onaangeroerd bleef.
   */
  closeTab(tabId: string): HdChartTabSnapshotV1 | null {
    const tabs = this._tabs();
    const index = tabs.findIndex(tab => tab.id === tabId);
    if (index === -1) {
      return null;
    }
    const wasActive = tabId === this._activeTabId();
    let remaining = tabs.filter(tab => tab.id !== tabId);
    if (remaining.length === 0) {
      remaining = [createDefaultHdChartTabSnapshot()];
    }
    this._tabs.set(remaining);
    if (!wasActive) {
      this.persistNow();
      return null;
    }
    const next = remaining[Math.min(index, remaining.length - 1)];
    this._activeTabId.set(next.id);
    this.persistNow();
    return next;
  }

  /**
   * Verplaatst een tab van `previousIndex` naar `currentIndex` (drag-reorder)
   * en persisteert de nieuwe volgorde direct. De actieve tab blijft ongewijzigd.
   */
  reorderTabs(previousIndex: number, currentIndex: number): void {
    const tabs = this._tabs();
    if (
      previousIndex === currentIndex
      || previousIndex < 0 || previousIndex >= tabs.length
      || currentIndex < 0 || currentIndex >= tabs.length
    ) {
      return;
    }
    const next = [...tabs];
    const [moved] = next.splice(previousIndex, 1);
    next.splice(currentIndex, 0, moved);
    this._tabs.set(next);
    this.persistNow();
  }

  /**
   * Sluit alle tabs links van `tabId`. Geeft het snapshot van de anker-tab
   * (`tabId`) terug wanneer de actieve tab tot de gesloten tabs behoorde —
   * de anker-tab wordt dan actief — anders `null`.
   */
  closeTabsToTheLeft(tabId: string): HdChartTabSnapshotV1 | null {
    return this.closeTabsBeyond(tabId, 'left');
  }

  /** Sluit alle tabs rechts van `tabId`. Zie {@link closeTabsToTheLeft}. */
  closeTabsToTheRight(tabId: string): HdChartTabSnapshotV1 | null {
    return this.closeTabsBeyond(tabId, 'right');
  }

  private closeTabsBeyond(tabId: string, side: 'left' | 'right'): HdChartTabSnapshotV1 | null {
    const tabs = this._tabs();
    const index = tabs.findIndex(tab => tab.id === tabId);
    if (index === -1) {
      return null;
    }
    const remaining = side === 'left' ? tabs.slice(index) : tabs.slice(0, index + 1);
    if (remaining.length === tabs.length) {
      return null;
    }
    const activeRemoved = !remaining.some(tab => tab.id === this._activeTabId());
    this._tabs.set(remaining);
    if (!activeRemoved) {
      this.persistNow();
      return null;
    }
    this._activeTabId.set(tabId);
    this.persistNow();
    return remaining.find(tab => tab.id === tabId) ?? remaining[0];
  }

  /**
   * Sluit álle tabs en laat één verse "Vrije modus"-tab achter. Geeft het toe
   * te passen snapshot terug.
   */
  resetToSingleTab(): HdChartTabSnapshotV1 {
    const tab = createDefaultHdChartTabSnapshot();
    this._tabs.set([tab]);
    this._activeTabId.set(tab.id);
    this.persistNow();
    return tab;
  }

  /** Flusht een eventueel hangende debounced write (aanroepen bij page-destroy). */
  flush(): void {
    if (this.persistTimerId != null) {
      this.persistNow();
    }
  }

  private schedulePersist(): void {
    if (this.persistTimerId != null) {
      clearTimeout(this.persistTimerId);
    }
    this.persistTimerId = setTimeout(() => this.persistNow(), PERSIST_DEBOUNCE_MS);
  }

  private persistNow(): void {
    if (this.persistTimerId != null) {
      clearTimeout(this.persistTimerId);
      this.persistTimerId = null;
    }
    try {
      const json = JSON.stringify({
        schemaVersion: 1,
        activeTabId: this._activeTabId(),
        tabs: this._tabs(),
      });
      localStorage.setItem(HD_CHART_TABS_STORAGE_KEY, json);
      notifyUserDataSavedLocally('appState', json);
    } catch {
      console.warn('hdChartTabs: wegschrijven naar localStorage mislukt.');
    }
  }
}
